import React, { useEffect, useState } from "react";
import authApi from "../../../api/authApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faDownload, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { Table, Button, Input, message, Card, Statistic, Drawer, Spin } from "antd";
import { format } from "date-fns";
import Modal from "../../../components/Modal";

let sendTimer: NodeJS.Timeout | null = null;

interface RefUser {
    userId: number;
    name: string;
    email: string;
    createTime: number;
    totalCommission: number;
    approvedCommission: number;
    pendingCommission: number;
    completedOrders: number;
    totalOrders: number;
}

interface RefData {
    userId: number;
    name: string;
    email: string;
    createTime: number;
    totalRefUsers: number;
    totalRefCommission: number;
    totalApprovedCommission: number;
    totalPendingCommission: number;
}

function Referral() {
    const [refs, setRefs] = useState<RefData[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [statistics, setStatistics] = useState({
        totalReferrers: 0,
        totalReferredUsers: 0,
        totalRefCommission: 0,
        totalApprovedCommission: 0,
        totalPendingCommission: 0,
        totalRefOrders: 0,
    });
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [filter, setFilter] = useState({
        limit: 20,
        page: 1,
        userId: "",
        name: "",
        email: "",
        sort: "-createTime",
    });
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [selectedRef, setSelectedRef] = useState<RefData | null>(null);
    const [detailUsers, setDetailUsers] = useState<RefUser[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        fetchStatistics();
    }, []);

    useEffect(() => {
        fetchRefs();
    }, [filter]);

    const fetchStatistics = async () => {
        setStatisticsLoading(true);
        try {
            const { data } = await authApi.getRefStatistics();
            setStatistics(data.statistics);
        } catch (error) {
            console.error("Failed to fetch statistics:", error);
            message.error("Không thể tải thống kê");
        } finally {
            setStatisticsLoading(false);
        }
    };

    const fetchRefs = async () => {
        setIsLoading(true);
        const queryString = Object.keys(filter)
            .reduce((acc: string[], cur) => {
                if (filter[cur as keyof typeof filter]) {
                    acc.push(`${cur}=${filter[cur as keyof typeof filter]}`);
                }
                return acc;
            }, [])
            .join("&");
        try {
            const { data } = await authApi.getAllRefs(queryString);
            setRefs(data.refs || []);
            setTotalUsers(data.totalUsers || 0);
        } catch (error) {
            console.error("Failed to fetch data:", error);
            message.error("Không thể tải dữ liệu");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (key: string, value: string) => {
        setFilter((prev) => ({
            ...prev,
            [key]: value,
            page: 1,
        }));
    };

    const onChangePageAndSize = (page: number, pageSize: number) => {
        setFilter((prev) => ({ ...prev, page, limit: pageSize }));
    };

    const handleViewDetail = async (record: RefData) => {
        setSelectedRef(record);
        setDetailDrawerOpen(true);
        setDetailLoading(true);
        try {
            const { data } = await authApi.getRefsByUserId(record.userId.toString());
            setDetailUsers(data.refUsers || []);
        } catch (error) {
            console.error("Failed to fetch detail:", error);
            message.error("Không thể tải chi tiết");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const queryString = Object.keys(filter)
                .reduce((acc: string[], cur) => {
                    if (filter[cur as keyof typeof filter] && cur !== "page") {
                        acc.push(`${cur}=${filter[cur as keyof typeof filter]}`);
                    }
                    return acc;
                }, [])
                .join("&");
            const response = await authApi.exportRefs(queryString);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `referrals_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success("Xuất file thành công");
        } catch (error) {
            console.error("Failed to export:", error);
            message.error("Không thể xuất file");
        }
    };

    const dataSource = refs.map((el, index) => ({
        key: index,
        id: el.userId,
        name: el.name,
        email: el.email,
        createTime: el.createTime,
        totalRefUsers: el.totalRefUsers,
        totalRefCommission: el.totalRefCommission,
        totalApprovedCommission: el.totalApprovedCommission || 0,
        totalPendingCommission: el.totalPendingCommission || 0,
    }));

    const columns = [
        {
            title: "User ID",
            dataIndex: "id",
            key: "id",
            width: 100,
        },
        {
            title: "Tên",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Số người giới thiệu",
            dataIndex: "totalRefUsers",
            key: "totalRefUsers",
            sorter: (a: any, b: any) => a.totalRefUsers - b.totalRefUsers,
            width: 200,
        },
        {
            title: "Tổng hoa hồng",
            dataIndex: "totalRefCommission",
            key: "totalRefCommission",
            sorter: (a: any, b: any) => a.totalRefCommission - b.totalRefCommission,
            render: (_: any, { totalRefCommission }: any) => {
                return parseFloat(totalRefCommission || 0).toLocaleString("vi-VN") + " đ";
            },
            width: 200,

        },
        {
            title: "Hoa hồng đã duyệt",
            dataIndex: "totalApprovedCommission",
            key: "totalApprovedCommission",
            sorter: (a: any, b: any) => a.totalApprovedCommission - b.totalApprovedCommission,
            render: (_: any, { totalApprovedCommission }: any) => {
                return <span style={{ color: "#52c41a" }}>{parseFloat(totalApprovedCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 200,
        },
        {
            title: "Hoa hồng chờ duyệt",
            dataIndex: "totalPendingCommission",
            key: "totalPendingCommission",
            sorter: (a: any, b: any) => a.totalPendingCommission - b.totalPendingCommission,
            render: (_: any, { totalPendingCommission }: any) => {
                return <span style={{ color: "#fa8c16" }}>{parseFloat(totalPendingCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 200,
        },
        {
            title: "Ngày tạo",
            dataIndex: "createTime",
            key: "createTime",
            render: (_: any, { createTime }: any) => {
                return format(new Date(createTime * 1000), "dd/MM/yyyy HH:mm");
            },
            width: 150,
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 100,
            render: (_: any, record: any) => {
                const refData = refs.find((r) => r.userId === record.id);
                return (
                    <Button type="link" icon={<FontAwesomeIcon icon={faEye} />} onClick={() => refData && handleViewDetail(refData)}>
                        Xem
                    </Button>
                );
            },
        },
    ];

    const detailColumns = [
        {
            title: "User ID",
            dataIndex: "userId",
            key: "userId",
            width: 100,
        },
        {
            title: "Tên",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Tổng đơn",
            dataIndex: "totalOrders",
            key: "totalOrders",
            width: 100,
        },
        {
            title: "Đơn hoàn thành",
            dataIndex: "completedOrders",
            key: "completedOrders",
            width: 120,
        },
        {
            title: "Tổng hoa hồng",
            dataIndex: "totalCommission",
            key: "totalCommission",
            render: (_: any, { totalCommission }: any) => {
                return parseFloat(totalCommission || 0).toLocaleString("vi-VN") + " đ";
            },
            width: 130,
        },
        {
            title: "Đã duyệt",
            dataIndex: "approvedCommission",
            key: "approvedCommission",
            render: (_: any, { approvedCommission }: any) => {
                return <span style={{ color: "#52c41a" }}>{parseFloat(approvedCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 130,
        },
        {
            title: "Chờ duyệt",
            dataIndex: "pendingCommission",
            key: "pendingCommission",
            render: (_: any, { pendingCommission }: any) => {
                return <span style={{ color: "#fa8c16" }}>{parseFloat(pendingCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 130,
        },
        {
            title: "Ngày tham gia",
            dataIndex: "createTime",
            key: "createTime",
            render: (_: any, { createTime }: any) => {
                return format(new Date(createTime * 1000), "dd/MM/yyyy HH:mm");
            },
            width: 150,
        },
    ];

    return (
        <div className="p-4">
            <div className="mb-4">
                <h1 className="text-2xl font-bold mb-4">Danh sách người giới thiệu</h1>

                {/* Statistics Cards */}
                {statisticsLoading ? (
                    <div className="flex justify-center py-8">
                        <Spin />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                        <Card>
                            <Statistic title="Tổng người giới thiệu" value={statistics.totalReferrers} valueStyle={{ color: "#3f8600" }} />
                        </Card>
                        <Card>
                            <Statistic title="Tổng người được giới thiệu" value={statistics.totalReferredUsers} valueStyle={{ color: "#1890ff" }} />
                        </Card>
                        <Card>
                            <Statistic title="Tổng hoa hồng" value={statistics.totalRefCommission} suffix="đ" valueStyle={{ color: "#cf1322" }} formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")} />
                        </Card>
                        <Card>
                            <Statistic title="Hoa hồng đã duyệt" value={statistics.totalApprovedCommission} suffix="đ" valueStyle={{ color: "#52c41a" }} formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")} />
                        </Card>
                        <Card>
                            <Statistic title="Hoa hồng chờ duyệt" value={statistics.totalPendingCommission} suffix="đ" valueStyle={{ color: "#fa8c16" }} formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")} />
                        </Card>
                        <Card>
                            <Statistic title="Tổng đơn hàng" value={statistics.totalRefOrders} valueStyle={{ color: "#722ed1" }} />
                        </Card>
                    </div>
                )}

                {/* Filter and Actions */}
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <Input placeholder="Tìm theo User ID" value={filter.userId} onChange={(e) => handleInputChange("userId", e.target.value)} style={{ width: 200 }} allowClear />
                    <Input placeholder="Tìm theo tên" value={filter.name} onChange={(e) => handleInputChange("name", e.target.value)} style={{ width: 200 }} allowClear />
                    <Input placeholder="Tìm theo email" value={filter.email} onChange={(e) => handleInputChange("email", e.target.value)} style={{ width: 200 }} allowClear />
                    <Button type="primary" icon={<FontAwesomeIcon icon={faDownload} />} onClick={handleExport}>
                        Xuất Excel
                    </Button>
                </div>
            </div>

            {/* Data Table */}
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={isLoading}
                pagination={{
                    current: filter.page,
                    pageSize: filter.limit,
                    total: totalUsers,
                    showSizeChanger: true,
                    showTotal: (total) => `Tổng ${total} người`,
                    onChange: onChangePageAndSize,
                }}
                scroll={{ x: "max-content" }}
            />

            {/* Detail Drawer */}
            <Drawer title={`Chi tiết người được giới thiệu bởi User ID: ${selectedRef?.userId}`} placement="right" width={800} onClose={() => setDetailDrawerOpen(false)} open={detailDrawerOpen}>
                {detailLoading ? (
                    <div className="flex justify-center py-8">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="mb-4">
                            <p>
                                <strong>Tên:</strong> {selectedRef?.name || "-"}
                            </p>
                            <p>
                                <strong>Email:</strong> {selectedRef?.email || "-"}
                            </p>
                            <p>
                                <strong>Tổng số người giới thiệu:</strong> {selectedRef?.totalRefUsers || 0}
                            </p>
                            <p>
                                <strong>Tổng hoa hồng:</strong> {parseFloat(selectedRef?.totalRefCommission.toString() || "0").toLocaleString("vi-VN")} đ
                            </p>
                            <p>
                                <strong>Hoa hồng đã duyệt:</strong> <span style={{ color: "#52c41a" }}>{parseFloat((selectedRef?.totalApprovedCommission || 0).toString()).toLocaleString("vi-VN")} đ</span>
                            </p>
                            <p>
                                <strong>Hoa hồng chờ duyệt:</strong> <span style={{ color: "#fa8c16" }}>{parseFloat((selectedRef?.totalPendingCommission || 0).toString()).toLocaleString("vi-VN")} đ</span>
                            </p>
                        </div>
                        <Table dataSource={detailUsers} columns={detailColumns} pagination={false} size="small" />
                    </>
                )}
            </Drawer>
        </div>
    );
}

export default Referral;
